// ────────────────────────────────────────────────────────────────────────────
// Federal + add-on tax engine. Pure functions — no React, no I/O. Each
// function is a primitive the projection can compose into a year's tax
// bill, and each is individually unit-testable against published examples.
//
// Design note: we track "ordinary" and "LTCG" taxable income separately,
// compute them against their own schedules, and then add FICA, Additional
// Medicare, NIIT, and the §121-style surtaxes where applicable. This is a
// deliberate simplification of Form 1040:
//   - No itemized deductions (standard deduction only). SALT cap, mortgage
//     interest deduction, charitable, etc. are out of scope; most FIRE
//     filers take the standard post-TCJA anyway.
//   - No AMT. Rarely binds post-TCJA for typical FIRE profiles.
//   - No QBI deduction, PTE/SALT workarounds.
//   - IRMAA uses current-year MAGI (the statute uses MAGI from two years
//     prior); good enough for planning-horizon modeling.
// ────────────────────────────────────────────────────────────────────────────

import {
  FEDERAL_ORDINARY_BRACKETS_2026,
  FICA_2026,
  FPL_2026,
  IRMAA_2026,
  LTCG_BRACKETS_2026,
  NIIT,
  RMD_DIVISORS,
  RMD_START_AGE,
  SS_TAX_THRESHOLDS,
  STANDARD_DEDUCTION_2026,
  type Bracket,
  type FilingStatus,
  type IrmaaTier,
} from './data';

export type { FilingStatus } from './data';
export { RMD_START_AGE } from './data';

// ── Federal ordinary income ─────────────────────────────────────────────────

/** Progressive tax on ordinary taxable income. */
export function federalOrdinaryTax(
  taxableIncome: number,
  status: FilingStatus,
): number {
  if (taxableIncome <= 0) return 0;
  return applyBrackets(taxableIncome, FEDERAL_ORDINARY_BRACKETS_2026[status]);
}

/** Marginal rate on the NEXT dollar of ordinary income. */
export function marginalOrdinaryRate(
  taxableIncome: number,
  status: FilingStatus,
): number {
  return marginalRate(taxableIncome, FEDERAL_ORDINARY_BRACKETS_2026[status]);
}

/** Remaining room before crossing into the next ordinary bracket.
 *  Useful for Roth-conversion headroom calculations. */
export function headroomToNextBracket(
  taxableIncome: number,
  status: FilingStatus,
): { currentRate: number; nextRate: number; nextFloor: number; headroom: number } {
  const brackets = FEDERAL_ORDINARY_BRACKETS_2026[status];
  let currentRate = brackets[0].rate;
  let nextFloor = Infinity;
  let nextRate = currentRate;
  for (let i = 0; i < brackets.length; i++) {
    if (brackets[i].over <= taxableIncome) {
      currentRate = brackets[i].rate;
      if (i + 1 < brackets.length) {
        nextFloor = brackets[i + 1].over;
        nextRate = brackets[i + 1].rate;
      } else {
        nextFloor = Infinity;
        nextRate = currentRate;
      }
    }
  }
  return {
    currentRate,
    nextRate,
    nextFloor,
    headroom: Math.max(0, nextFloor - taxableIncome),
  };
}

function applyBrackets(income: number, brackets: Bracket[]): number {
  let tax = 0;
  for (let i = 0; i < brackets.length; i++) {
    const floor = brackets[i].over;
    const ceiling = i + 1 < brackets.length ? brackets[i + 1].over : Infinity;
    if (income <= floor) break;
    const slice = Math.min(income, ceiling) - floor;
    tax += slice * brackets[i].rate;
    if (income <= ceiling) break;
  }
  return tax;
}

function marginalRate(income: number, brackets: Bracket[]): number {
  let current = brackets[0].rate;
  for (const b of brackets) {
    if (income >= b.over) current = b.rate;
  }
  return current;
}

export function standardDeduction(status: FilingStatus): number {
  return STANDARD_DEDUCTION_2026[status];
}

// ── Federal LTCG + qualified dividends ──────────────────────────────────────
// LTCG is "stacked on top" of ordinary income. The taxpayer's ordinary
// taxable income fills the lower brackets first; LTCG bracket thresholds
// are then compared against (ordinary + LTCG). Any portion of LTCG that
// falls below the 0% ceiling is untaxed.

export function federalLTCGTax(
  ltcg: number,
  ordinaryTaxable: number,
  status: FilingStatus,
): number {
  if (ltcg <= 0) return 0;
  const thresholds = LTCG_BRACKETS_2026[status];
  const totalIncome = ordinaryTaxable + ltcg;
  let tax = 0;
  // 0% slice: the portion of LTCG below the zero-bracket ceiling.
  const zeroTop = Math.max(0, thresholds.zero - ordinaryTaxable);
  const zeroSlice = Math.min(ltcg, zeroTop);
  let remainingLtcg = ltcg - zeroSlice;
  // 15% slice: between zero ceiling and 15% ceiling.
  if (remainingLtcg > 0) {
    const fifteenTop = Math.max(
      0,
      thresholds.fifteen - Math.max(ordinaryTaxable, thresholds.zero),
    );
    const fifteenSlice = Math.min(remainingLtcg, fifteenTop);
    tax += fifteenSlice * 0.15;
    remainingLtcg -= fifteenSlice;
  }
  // 20% slice: the rest.
  if (remainingLtcg > 0) {
    tax += remainingLtcg * 0.20;
  }
  // If ordinary income already pushed past a threshold, adjust (this
  // happens implicitly through the zeroTop/fifteenTop computations above).
  void totalIncome;
  return tax;
}

/** Marginal LTCG rate at a given current ordinary+LTCG total. */
export function marginalLTCGRate(
  ordinaryTaxable: number,
  ltcg: number,
  status: FilingStatus,
): number {
  const top = ordinaryTaxable + ltcg;
  const t = LTCG_BRACKETS_2026[status];
  if (top < t.zero) return 0;
  if (top < t.fifteen) return 0.15;
  return 0.20;
}

// ── Social Security benefit taxation ────────────────────────────────────────
// Provisional income = AGI (excluding SS) + tax-exempt interest + 50% of SS.
// Below low threshold: 0% of SS is taxable.
// Between thresholds: up to 50% of SS is taxable.
// Above high threshold: up to 85% of SS is taxable.
// Statute hasn't moved since 1984, so these thresholds bite HARD over time.

export function ssTaxableAmount(
  annualSSBenefits: number,
  otherIncome: number,
  status: FilingStatus,
): number {
  if (annualSSBenefits <= 0) return 0;
  const t = SS_TAX_THRESHOLDS[status];
  const provisional = otherIncome + annualSSBenefits * 0.5;
  if (provisional <= t.fiftyPct) return 0;
  if (provisional <= t.eightyFivePct) {
    // Up to 50% of SS, limited by (provisional - fiftyPct)/2.
    return Math.min(
      annualSSBenefits * 0.5,
      (provisional - t.fiftyPct) * 0.5,
    );
  }
  // Up to 85% of SS, summing the 50% and 85% tranches.
  const fiftyTranche = Math.min(annualSSBenefits * 0.5, (t.eightyFivePct - t.fiftyPct) * 0.5);
  const eightyFiveTranche = (provisional - t.eightyFivePct) * 0.85;
  return Math.min(annualSSBenefits * 0.85, fiftyTranche + eightyFiveTranche);
}

// ── FICA (wage tax) ─────────────────────────────────────────────────────────

export function fica(wages: number, status: FilingStatus): {
  socialSecurity: number;
  medicare: number;
  additionalMedicare: number;
  total: number;
} {
  const w = Math.max(0, wages);
  const ss = Math.min(w, FICA_2026.ssWageBase) * FICA_2026.ssRate;
  const medicare = w * FICA_2026.medicareRate;
  const addlThreshold = FICA_2026.additionalMedicareThreshold[status];
  const additionalMedicare =
    w > addlThreshold ? (w - addlThreshold) * FICA_2026.additionalMedicareRate : 0;
  return {
    socialSecurity: ss,
    medicare,
    additionalMedicare,
    total: ss + medicare + additionalMedicare,
  };
}

// ── Net Investment Income Tax (§1411) ───────────────────────────────────────

export function niit(
  investmentIncome: number,
  magi: number,
  status: FilingStatus,
): number {
  const threshold = NIIT.threshold[status];
  if (magi <= threshold) return 0;
  const overage = magi - threshold;
  const taxable = Math.min(Math.max(0, investmentIncome), overage);
  return taxable * NIIT.rate;
}

// ── IRMAA (Medicare Part B + D surcharges) ──────────────────────────────────

export function irmaaTier(magi: number, status: FilingStatus): IrmaaTier {
  for (const tier of IRMAA_2026) {
    const cap = status === 'single' ? tier.singleCap : tier.marriedCap;
    if (magi < cap) return tier;
  }
  return IRMAA_2026[IRMAA_2026.length - 1];
}

/** Annual IRMAA cost above standard Part B (+Part D surcharge). Per person. */
export function irmaaAnnualSurcharge(magi: number, status: FilingStatus): number {
  const tier = irmaaTier(magi, status);
  const standard = IRMAA_2026[0];
  const partBSurcharge = Math.max(0, tier.partBMonthly - standard.partBMonthly);
  return (partBSurcharge + tier.partDSurcharge) * 12;
}

// ── Required Minimum Distributions ─────────────────────────────────────────

export function requiredMinDistribution(
  retirementBalance: number,
  age: number,
): number {
  if (age < RMD_START_AGE) return 0;
  const divisor = RMD_DIVISORS[Math.min(age, 120)] ?? RMD_DIVISORS[120];
  return retirementBalance / divisor;
}

// ── Combined annual tax bill ────────────────────────────────────────────────

export interface AnnualTaxInput {
  status: FilingStatus;
  /** W-2 wages (FICA applies here). */
  wages: number;
  /** Ordinary income beyond wages: pretax IRA withdrawals, pension, taxable
   *  interest, short-term capital gains, Roth-conversion amounts, etc. */
  otherOrdinary: number;
  /** Long-term capital gains + qualified dividends. */
  ltcg: number;
  /** Annual Social Security benefits received this year (gross). */
  ssBenefits: number;
  /** Net investment income (subject to NIIT if MAGI > threshold).
   *  Typically interest + dividends + LTCG + non-active rental income. */
  netInvestmentIncome: number;
  /** State tax rate applied to ordinary + LTCG (flat-rate approximation). */
  stateRate: number;
}

export interface AnnualTaxResult {
  /** Income inputs echoed back for UI display — makes it clear WHERE the
   *  income came from and why tax might be low (e.g., all LTCG in 0% bracket). */
  income: {
    wages: number;
    otherOrdinary: number;
    ltcg: number;
    ssBenefits: number;
    /** Sum of all four — total dollars flowing in this year. */
    grossIncome: number;
  };
  /** Taxable ordinary income after standard deduction. */
  taxableOrdinary: number;
  /** Taxable SS benefits (50% / 85% rule). */
  ssTaxable: number;
  /** Federal tax on ordinary income (incl. SS tax portion). */
  federalOrdinaryTax: number;
  /** Federal tax on LTCG (stacked on top of ordinary). */
  federalLTCGTax: number;
  /** Total FICA (SS 6.2% + Medicare 1.45%). */
  ficaTotal: number;
  /** Additional 0.9% Medicare surtax on high wages. */
  additionalMedicareTax: number;
  /** §1411 Net Investment Income Tax. */
  niitTax: number;
  /** State tax (flat rate × ordinary + LTCG). */
  stateTax: number;
  /** Total tax burden: federal + state + FICA. */
  totalTax: number;
  /** MAGI used for IRMAA, NIIT, ACA. */
  magi: number;
  /** Current ordinary-bracket marginal rate. */
  marginalOrdinaryRate: number;
  /** Current LTCG marginal rate. */
  marginalLTCGRate: number;
  /** How much more ordinary income the filer could earn before hitting the
   *  next bracket (critical for Roth conversion planning). */
  headroomToNextBracket: number;
  /** Effective rate = totalTax / (gross ordinary + LTCG + SS). */
  effectiveRate: number;
}

export function computeAnnualTax(input: AnnualTaxInput): AnnualTaxResult {
  const { status, wages, otherOrdinary, ltcg, ssBenefits, netInvestmentIncome, stateRate } = input;

  // SS taxation uses the provisional-income rule. "Other income" excludes
  // SS itself; we also add LTCG (which is technically AGI-inclusive).
  const ssTaxable = ssTaxableAmount(ssBenefits, wages + otherOrdinary + ltcg, status);
  const grossOrdinary = wages + otherOrdinary + ssTaxable;
  const taxableOrdinary = Math.max(0, grossOrdinary - standardDeduction(status));

  const federalOrd = federalOrdinaryTax(taxableOrdinary, status);
  const federalLt = federalLTCGTax(ltcg, taxableOrdinary, status);

  const ficaBreakdown = fica(wages, status);
  const niitTax = niit(netInvestmentIncome, taxableOrdinary + ltcg, status);

  // State tax: flat rate on ordinary + LTCG (simplification — most states
  // don't have a separate LTCG rate; CA is the notable exception).
  const stateTax = (taxableOrdinary + ltcg) * Math.max(0, stateRate);

  const totalTax =
    federalOrd +
    federalLt +
    ficaBreakdown.socialSecurity +
    ficaBreakdown.medicare +
    ficaBreakdown.additionalMedicare +
    niitTax +
    stateTax;

  const grossIncome = wages + otherOrdinary + ltcg + ssBenefits;
  const effectiveRate = grossIncome > 0 ? totalTax / grossIncome : 0;

  const head = headroomToNextBracket(taxableOrdinary, status);

  return {
    income: {
      wages,
      otherOrdinary,
      ltcg,
      ssBenefits,
      grossIncome,
    },
    taxableOrdinary,
    ssTaxable,
    federalOrdinaryTax: federalOrd,
    federalLTCGTax: federalLt,
    ficaTotal: ficaBreakdown.socialSecurity + ficaBreakdown.medicare,
    additionalMedicareTax: ficaBreakdown.additionalMedicare,
    niitTax,
    stateTax,
    totalTax,
    magi: taxableOrdinary + ltcg, // simplified — true MAGI adds back some deductions
    marginalOrdinaryRate: head.currentRate,
    marginalLTCGRate: marginalLTCGRate(taxableOrdinary, ltcg, status),
    headroomToNextBracket: head.headroom,
    effectiveRate,
  };
}

// ── ACA premium tax credit (simplified) ─────────────────────────────────────
// Very rough model of the ACA subsidy curve: the "Applicable Figure" varies
// from 0% of income (at 100% FPL) up to 8.5% (above 400% FPL) — the ARPA/IRA
// extension removed the original cliff through 2025 and was extended again
// through 2026 by the OBBBA. Taxpayers owe the lesser of this percentage or
// the benchmark silver-plan cost; above that, a subsidy covers the rest.

export function acaApplicableFigurePct(magi: number, householdSize: number): number {
  const fplAmt = FPL_2026.singleHousehold + (householdSize - 1) * FPL_2026.perAdditionalPerson;
  const pctFpl = fplAmt > 0 ? (magi / fplAmt) * 100 : 0;
  // Post-IRA sliding scale (simplified):
  //   0-150% FPL: 0%
  //   150-200% FPL: 0% to 2%
  //   200-250% FPL: 2% to 4%
  //   250-300% FPL: 4% to 6%
  //   300-400% FPL: 6% to 8.5%
  //   400%+ FPL:   8.5% (no cliff)
  if (pctFpl <= 150) return 0;
  if (pctFpl <= 200) return linterp(pctFpl, 150, 200, 0, 0.02);
  if (pctFpl <= 250) return linterp(pctFpl, 200, 250, 0.02, 0.04);
  if (pctFpl <= 300) return linterp(pctFpl, 250, 300, 0.04, 0.06);
  if (pctFpl <= 400) return linterp(pctFpl, 300, 400, 0.06, 0.085);
  return 0.085;
}

function linterp(x: number, x0: number, x1: number, y0: number, y1: number): number {
  return y0 + ((y1 - y0) * (x - x0)) / (x1 - x0);
}
